import { MouseEventHandler, useMemo } from 'react'

import { createStyles } from 'antd-style'

import { pick } from '@eastcoal/common/utils/object'

import { useContractDesignerStore } from '../../../designer/Store/hooks/useStore'

import { TableVariablePlaceholderProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  table: {
    userSelect: 'none',
    cursor: 'pointer',
    ['&.is-selected']: {
      borderColor: token.colorPrimaryBorder,
      backgroundColor: token.colorPrimaryBg
    }
  }
}))

export const TableVariableCellClassName =
  'contract-desinger-table-variable-cell'

export const TableVariablePlaceholder = ({
  nodeKey,
  variable,
  tableStyleConfig: {
    borderFormat,
    fontFamily,
    lineHeight,
    textAlign,
    verticalAlign
  },
  headerStyleConfig: {
    fontSize: headerFontSize,
    fontWeight: headerFontWeight,
    color: headerColor,
    backgroundColor: headerBackgroundColor
  },
  bodyStyleConfig
}: TableVariablePlaceholderProps) => {
  const columns = useMemo(
    () =>
      variable.children.map(
        ({ id, name, property, width, textAlign, color, fontWeight }) => ({
          key: id,
          dataIndex: `${property}`,
          title: `${name}`,
          className: TableVariableCellClassName,
          style: {
            width,
            textAlign,
            color,
            fontWeight
          }
        })
      ),
    [variable]
  )

  const dataSource = useMemo(
    () =>
      Array.from({ length: 3 }).map((_, index) => {
        return variable.children.reduce((acc, { property }) => {
          acc[`${property}`] = '待填充'
          return acc
        }, {})
      }),
    [variable]
  )

  const { activedVariableNodeKey, setActivedVariableNodeKey } =
    useContractDesignerStore(state =>
      pick(state, ['activedVariableNodeKey', 'setActivedVariableNodeKey'])
    )
  const isSelected = activedVariableNodeKey === nodeKey

  const handleClick: MouseEventHandler<HTMLTableElement> = event => {
    // 防止事件冒泡到编辑器导致 selection 变为 null
    event.stopPropagation()
    setActivedVariableNodeKey(isSelected ? undefined : nodeKey)
  }

  const { cx, styles } = useStyles()

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <table
      style={{ fontFamily, lineHeight }}
      className={cx(styles.table, {
        'is-selected': isSelected,
        [`border-${borderFormat}`]: !!borderFormat,
        [`align-${textAlign}`]: !!textAlign,
        [`vertical-${verticalAlign}`]: !!verticalAlign,
        ['strong-header']: !!headerFontWeight
      })}
      onClick={handleClick}
    >
      <thead
        style={{
          fontSize: headerFontSize,
          color: headerColor,
          backgroundColor: headerBackgroundColor
        }}
      >
        <tr>
          {columns.map(({ key, title, className, style }) => (
            <th key={key} className={className} style={style}>
              {title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody style={bodyStyleConfig}>
        {dataSource.map((row, index) => (
          <tr key={index}>
            {columns.map(({ key, dataIndex, className, style }) => (
              <td key={key} className={className} style={style}>
                {row[dataIndex]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
